import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { AppIcon } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CollegeChip } from '@backrs_ui/grow-ui/src/components/MyColleges/CollegeChip';
import { StoryDocument } from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { useGetUserCollegesQuery } from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.api';

import { Box, Button, Link, SxProps, Theme, Tooltip, Typography } from '@mui/material';

import { useAgentContext } from './OwnProfileSidebar.agentContext';
import { ProfileDocumentSection } from '../atoms/ProfileDocumentSection';

const STRINGS = appStrings.modules.profile.public;
const SIDEBAR_STRINGS = STRINGS.sidebar;

const COLLEGE_PREVIEW_COUNT = 9;

const COMING_SOON_SECTIONS = [
  { id: 'majors', title: SIDEBAR_STRINGS.majors },
  { id: 'career-paths', title: SIDEBAR_STRINGS.careerPaths },
  { id: 'jobs-internships', title: SIDEBAR_STRINGS.jobsInternships },
] as const;

type OwnProfileSidebarProps = Readonly<{
  resume?: StoryDocument;
  transcript?: StoryDocument;
}>;

export const OwnProfileSidebar = ({ resume, transcript }: OwnProfileSidebarProps) => (
  <Box id="profile-sidebar" sx={sidebarSx}>
    <Box sx={cardSx}>
      <CollegeListSection />

      {COMING_SOON_SECTIONS.map(({ id, title }) => (
        <Tooltip key={id} title={SIDEBAR_STRINGS.comingSoon} placement="top" arrow>
          <Box id={`profile-sidebar-${id}`} sx={comingSoonRowSx}>
            <Typography variant="text-lg-semibold">{title}</Typography>
            <AppIcon iconName="ChevronDown" diameter="20px" color="Colors/Text/text-disabled" />
          </Box>
        </Tooltip>
      ))}
    </Box>

    <ProfileDocumentSection id="profile-sidebar-resume" title={STRINGS.resume} document={resume} />
    <ProfileDocumentSection
      id="profile-sidebar-transcript"
      title={SIDEBAR_STRINGS.transcript}
      document={transcript}
    />
  </Box>
);

const CollegeListSection = () => {
  const navigate = useNavigate();
  const { data, isError } = useGetUserCollegesQuery(undefined);

  const colleges = data?.colleges.slice(0, COLLEGE_PREVIEW_COUNT);

  useAgentContext(colleges);

  return (
    <Box id="profile-sidebar-college-list" sx={collegeListSx}>
      <Typography variant="text-lg-semibold">{SIDEBAR_STRINGS.collegeList}</Typography>

      {colleges?.length === 0 && (
        <Button
          id="profile-sidebar-start-college-list"
          variant="contained"
          size="sm"
          hierarchy="Secondary gray"
          onClick={() => navigate(APP_ROUTE.MY_COLLEGES)}
          sx={startButtonSx}
        >
          {SIDEBAR_STRINGS.startCollegeList}
        </Button>
      )}

      {!!colleges?.length && (
        <Box sx={chipGroupSx}>
          {colleges.map((college) => (
            <CollegeChip
              key={college.id}
              id={`profile-sidebar-college-${college.id}`}
              college={college}
              size="sm"
            />
          ))}
        </Box>
      )}

      {(!!colleges?.length || isError) && (
        <Link
          id="profile-sidebar-college-list-view-all"
          component={RouterLink}
          to={APP_ROUTE.MY_COLLEGES}
          variant="text-sm-semibold"
          sx={viewAllSx}
        >
          {SIDEBAR_STRINGS.viewAll}
        </Link>
      )}
    </Box>
  );
};

const sidebarSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
});

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: backrs.spacing['spacing-xl'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  '& > *:not(:last-child)': {
    borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  },
});

const collegeListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  paddingBottom: backrs.spacing['spacing-xl'],
});

const chipGroupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: backrs.spacing['spacing-sm'],
  '& > *': { width: '100%' },
});

const viewAllSx: SxProps<Theme> = { alignSelf: 'flex-end' };

const startButtonSx: SxProps<Theme> = { alignSelf: 'flex-start' };

const comingSoonRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingY: backrs.spacing['spacing-xl'],
  color: backrs.colors['Colors/Text/text-disabled'],
  cursor: 'default',
  '&:last-child': { paddingBottom: 0 },
});
